<template>
  <div class="atb-divide-y atb-divide-gray-300 atb-py-6">
    <Card class="card-size atb-ml-20 atb-pt-6">
      <template #body>
        <Form>
          <div
            class="atb-grid atb-grid-cols-2 atb-gap-y-4 atb-gap-x-14 atb-px-2 atb-py-4 form-section"
          >
            <div>
              <FormField
                :label="$t('roleName')"
                required="true"
                form-field-class="input-field-width"
                :message="roleNameErrorMsg"
                :message-type="'error'"
              >
                <Textbox
                  id="roleNameTextbox"
                  v-model="roleDataValue.name"
                  type="text"
                  :state="roleNameErrorMsg ? 'error' : 'basic'"
                  @update:modelValue="roleNameErrorMsg = ''"
                />
              </FormField>
            </div>
            <div>
              <!-- TODO:Implement virtual scrolling for client dropdown to improve performance with large number of clients -->
              <Dropdown
                v-model="selectedClientName"
                :items="clientItems"
                :key="clientItems.length"
                :label="$t('selectClient')"
                filterable
                field-label-class="option-class"
                active-item-class="d-active"
                :get-option-label="(e: any) => e.clientName"
                :get-option-value="(e: any) => e.clientName"
                title="selectedClientName"
              />
            </div>
            <div>
              <Checkbox
                id="ssoDefaultRoleCheckbox"
                v-model:checked="roleDataValue.ssoDefaultRole"
                :label="$t('ssoDefaultRole')"
              />
            </div>
          </div>
        </Form>

        <div class="accessRights-container atb-mt-4 atb-mx-2">
          <div class="atb-font-bold atb-py-4 atb-flex atb-align-center">
            {{ $t('Permissions') }}
          </div>
          <div
            v-for="group in accessRightsGroups"
            :key="group.groupName"
            class="accessRights-table atb-mb-4"
          >
            <div class="atb-font-semibold atb-px-4 atb-py-2 atb-bg-gray-100">
              {{ group.groupName }}
            </div>
            <div class="permissions-table grid-top-border">
              <div class="permissions-header-row">
                <div class="permissions-col permissions-col-access">
                  {{ $t('Access') }}
                </div>
                <div class="permissions-col permissions-col-level">
                  {{ $t('Access Level') }}
                </div>
                <div class="permissions-col permissions-col-description">
                  {{ $t('Description') }}
                </div>
              </div>
              <div
                v-for="accessRight in group.accessRights"
                :key="accessRight.id"
                class="permissions-data-row"
              >
                <div class="permissions-col permissions-col-access">
                  {{ accessRight.name }}
                </div>
                <div class="permissions-col permissions-col-level">
                  <select
                    v-model="accessRight.accessLevel"
                    class="access-level-select"
                    :disabled="readOnly"
                  >
                    <option value="NOACCESS">No Access</option>
                    <option value="READONLY">Read Only</option>
                    <option value="FULLACCESS">Full Access</option>
                  </select>
                </div>
                <div class="permissions-col permissions-col-description">
                  {{ accessRight.description }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          class="atb-flex atb-flex-row atb-justify-end atb-gap-x-8 atb-pt-8 atb-px-2"
        >
          <Button
            id="roleTabDeleteButton"
            type="submit"
            motif="basic"
            :label="$t('delete')"
            :title="$t('delete')"
            :disabled="readOnly || isDeleteDisabled"
            @click="deleteRole"
          />
          <Button
            id="roleTabSaveButton"
            type="submit"
            motif="primary"
            :label="$t('save')"
            :title="$t('save')"
            :disabled="readOnly"
            @click="saveRole"
          />
        </div>
      </template>
    </Card>
  </div>
</template>

<script lang="ts" setup>
  import { ref, onMounted, toRefs, watch, computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import Card from '@atb-ui/eds-vue/src/components/Card.vue';
  import Form from '@atb-ui/eds-vue/src/components/Form/Form.vue';
  import FormField from '@atb-ui/eds-vue/src/components/Form/FormField.vue';
  import Textbox from '@atb-ui/eds-vue/src/components/Textbox.vue';
  import Button from '@atb-ui/eds-vue/src/components/Button.vue';
  import Dropdown from '@atb-ui/eds-vue/src/components/Dropdown.vue';
  import Checkbox from '@atb-ui/eds-vue/src/components/Checkbox.vue';
  import { useRoleStore } from '@/app/features/admin/roles/role-store';
  import { useClientStore } from '@/app/features/admin/clients/client-store';
  import { useAlertStore } from '@/app/shared/stores/alert-store';
  import { Motif } from '@/app/shared/enums/motif-enum';
  import { RoleData } from '../role-data.interface';
  import { RoleAccess } from '../role-access-interface';
  import { Client } from '@/app/shared/types/models/client.interface';

  const { t } = useI18n();
  const roleStore = useRoleStore();
  const clientStore = useClientStore();
  const alertStore = useAlertStore();

  interface RoleTabEvent {
    (_event: 'saveRoleData', _params: RoleData): void;
    (_event: 'deleteRoleData', _params: number | string): void;
  }

  interface RoleTabProps {
    roleId: number;
    roleData: RoleData;
    readOnly: boolean;
  }

  const props = defineProps<RoleTabProps>();
  const emit = defineEmits<RoleTabEvent>();
  const { roleId, roleData, readOnly } = toRefs(props);
  const roleDataValue = roleData.value;

  const roleNameErrorMsg = ref('');
  const clientIdErrorMsg = ref('');
  const availableAccessRights = ref<RoleAccess[]>([]);
  const clientItems = computed(() => clientStore.clientList.content || []);

  const selectedClientName = computed({
    get: () => {
      if (roleDataValue.clientId && roleDataValue.clientId !== 0) {
        const existing = clientItems.value.find(
          (client: Client) => client.id === roleDataValue.clientId
        );
        return existing ? existing.clientName : '';
      }
      return '';
    },
    set: (value: string) => {
      const selectedClient = clientItems.value.find(
        (client: Client) => client.clientName === value
      );
      if (selectedClient) {
        roleDataValue.clientId = selectedClient.id;
      }
    },
  });

  interface AccessRightsGroup {
    groupName: string;
    accessRights: RoleAccess[];
  }

  const accessRightsGroups = ref<AccessRightsGroup[]>([]);

  const applySavedAccessRightsToGroups = () => {
    const existingAccessRights = roleData.value.accessRights || [];
    if (existingAccessRights.length === 0) {
      return;
    }

    const accessRightsMap = new Map(
      existingAccessRights.map((ar: any) => [ar.id, ar.accessLevel])
    );

    accessRightsGroups.value.forEach((group) => {
      group.accessRights.forEach((accessRight) => {
        const savedAccessLevel = accessRightsMap.get(accessRight.id);
        if (savedAccessLevel) {
          accessRight.accessLevel = savedAccessLevel;
        }
      });
    });
  };

  onMounted(async () => {
    await clientStore.fetchClients();

    const accessRightsList = await roleStore.getRoleAccessList();
    availableAccessRights.value = JSON.parse(JSON.stringify(accessRightsList));

    const groupsMap = new Map<string, RoleAccess[]>();
    availableAccessRights.value.forEach((accessRight) => {
      const groupName = accessRight.groupName || 'Other';
      accessRight.accessLevel = 'NOACCESS';
      if (!groupsMap.has(groupName)) {
        groupsMap.set(groupName, []);
      }
      groupsMap.get(groupName)?.push(accessRight);
    });

    accessRightsGroups.value = Array.from(groupsMap.entries())
      .map(([groupName, accessRights]) => ({
        groupName,
        accessRights,
      }))
      .sort((a, b) => a.groupName.localeCompare(b.groupName));

    applySavedAccessRightsToGroups();
  });

  watch(
    () => roleData.value.accessRights,
    () => {
      applySavedAccessRightsToGroups();
    }
  );

  const isDeleteDisabled = computed((): boolean => {
    return roleId.value === -1;
  });

  const validateForm = (): boolean => {
    roleNameErrorMsg.value = '';
    clientIdErrorMsg.value = '';

    let hasError = false;

    if (!roleDataValue.name) {
      roleNameErrorMsg.value = t('emptyField');
      hasError = true;
    }

    return !hasError;
  };

  const saveRole = () => {
    if (!validateForm()) {
      return;
    }

    const allAccessRightsWithAccessLevels: any[] = [];
    accessRightsGroups.value.forEach((group) => {
      group.accessRights.forEach((accessRight) => {
        const accessLevel = accessRight.accessLevel || 'NOACCESS';
        if (accessLevel === 'FULLACCESS' || accessLevel === 'READONLY') {
          allAccessRightsWithAccessLevels.push({
            id: accessRight.id,
            name: accessRight.name,
            accessLevel: accessLevel,
            description: accessRight.description,
            groupName: accessRight.groupName,
            writable: accessRight.writable,
            readable: accessRight.readable,
            createdAt: accessRight.createdAt,
            updatedAt: accessRight.updatedAt,
          });
        }
      });
    });

    roleDataValue.accessRights = allAccessRightsWithAccessLevels;

    const roleDataToSave = { ...roleDataValue };
    emit('saveRoleData', roleDataToSave);

    alertStore.showNotification(
      t('roleSavedSuccessfully'),
      t('role'),
      Motif.SUCCESS
    );
  };

  const deleteRole = () => {
    const roleId = roleDataValue.id;
    if (roleId != null) {
      emit('deleteRoleData', roleId);
    } else {
      console.error('Cannot delete: role ID is missing.');
    }
  };
</script>

<style scoped>
  .card-size {
    width: 70%;
    height: 100%;
  }

  .form-section {
    position: relative;
    z-index: 1000;
  }

  .input-field-width {
    width: 100%;
  }

  .option-class {
    text-align: left;
    padding: 10px 16px;
  }

  .d-active {
    background-color: #426da9;
    color: white;
  }

  .card-title {
    font-weight: 500;
    font-size: 16px;
    color: #767676;
  }

  .tools-header-tab {
    align-items: center;
    height: 50px;
  }

  .accessRights-container {
    position: relative;
    z-index: 1;
  }

  .accessRights-table {
    border: 1px solid #d6d6d6;
    border-radius: 6px;
  }

  .grid-top-border {
    border-top: 1px solid #d6d6d6;
  }

  .permissions-table {
    width: 100%;
    overflow: hidden;
    border-radius: 0 0 6px 6px;
  }

  .permissions-header-row,
  .permissions-data-row {
    display: grid;
    width: 100%;
    grid-template-columns: minmax(180px, 1.2fr) minmax(170px, 1fr) minmax(
        360px,
        3fr
      );
    column-gap: 0;
    align-items: stretch;
  }

  .permissions-header-row {
    min-height: 42px;
    font-weight: 600;
    font-size: 14px;
    border-bottom: 1px solid #e7e7e7;
    background: #f8fafc;
  }

  .permissions-data-row {
    min-height: 42px;
    border-bottom: 1px solid #efefef;
    background: #fff;
    transition: background-color 0.15s ease;
  }

  .permissions-data-row:hover {
    background: #f9fbff;
  }

  .permissions-data-row:last-child {
    border-bottom: none;
  }

  .permissions-col {
    padding: 8px 12px;
    font-size: 14px;
    color: #333;
    box-sizing: border-box;
    line-height: 1.4;
    display: flex;
    align-items: center;
  }

  .permissions-col-level {
    padding: 6px 10px;
  }

  .permissions-col-description {
    white-space: normal;
    word-break: break-word;
    align-items: flex-start;
  }

  .access-level-select {
    width: 100%;
    min-height: 34px;
    border: 1px solid #c8ced8;
    border-radius: 4px;
    padding: 4px 8px;
    font-size: 14px;
    box-sizing: border-box;
    background: #fff;
    color: #333;
    font-family: inherit;
  }

  .access-level-select:focus {
    outline: none;
    border-color: #426da9;
    box-shadow: 0 0 0 1px rgba(66, 109, 169, 0.2);
  }

  .access-level-select:disabled {
    background: #f3f4f6;
    color: #7a7a7a;
    cursor: not-allowed;
  }

  :deep(.eds-dropdown-menu) {
    z-index: 10001 !important;
  }

  :deep(.dropdown-container) {
    z-index: 10001 !important;
  }

  :deep(.dropdown-menu) {
    z-index: 10001 !important;
    position: relative;
  }
</style>
